import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import Logo from '@/components/Logo'
import AuthBackgroundShape from '@/components/AuthBackgroundShape'
import { supabase } from '@/lib/supabase'

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      })

      if (error) {
        toast.error(error.message || 'Failed to send reset email')
        setIsLoading(false)
        return
      }

      setIsSubmitted(true)
      toast.success('Password reset email sent! Check your inbox.')
    } catch {
      toast.error('Failed to send reset email. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (isSubmitted) {
    return (
      <div className='relative flex h-auto min-h-screen items-center justify-center overflow-x-hidden px-4 py-10 sm:px-6 lg:px-8'>
        <div className='absolute'>
          <AuthBackgroundShape />
        </div>

        <Card className='z-1 w-full border-none shadow-md sm:max-w-lg'>
          <CardHeader className='gap-3 items-center justify-center'>
            <Logo className='gap-3 mx-auto' showText={false} />
            <div>
              <CardTitle className='mb-1.5 text-2xl'>Check your email</CardTitle>
            </div>
          </CardHeader>

          <CardContent>
            <div className='space-y-4'>
              <p className='text-muted-foreground text-center'>
                We've sent a password reset link to <strong>{email}</strong>
              </p>
              <p className='text-sm text-muted-foreground text-center'>
                Click the link in the email to reset your password. The link will expire in 1 hour.
              </p>
              <div className='pt-4'>
                <Link to='/auth/login'>
                  <Button variant='outline' className='w-full'>
                    Back to sign in
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className='relative flex h-auto min-h-screen items-center justify-center overflow-x-hidden px-4 py-10 sm:px-6 lg:px-8'>
      <div className='absolute'>
        <AuthBackgroundShape />
      </div>

      <Card className='z-1 w-full border-none shadow-md sm:max-w-lg'>
        <CardHeader className='gap-3 items-center justify-center'>
          <Logo className='gap-3 mx-auto' showText={false} />
          <div>
            <CardTitle className='mb-1.5 text-2xl'>Reset your password</CardTitle>
          </div>
        </CardHeader>

        <CardContent>
          <div className='space-y-4'>
            <p className='text-muted-foreground text-center text-sm'>
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <form className='space-y-4' onSubmit={handleSubmit}>
              <div className='space-y-1'>
                <Label htmlFor='email' className='leading-5'>
                  Email address*
                </Label>
                <Input
                  type='email'
                  id='email'
                  placeholder='Enter your email address'
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <Button className='w-full' type='submit' disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send reset link'}
              </Button>
            </form>

            <p className='text-muted-foreground text-center text-sm'>
              Remember your password?{' '}
              <Link to='/auth/login' className='text-card-foreground hover:underline'>
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ForgotPasswordPage

